import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { billingApi } from '@/api';
import { posApi, type StorePaymentGateway } from '@/api/pos.api';
import { customersApi } from '@/api/customers.api';
import { focusElement, useKeyboardContext, useKeyboardShortcuts } from '@/core/keyboard';
import { POS_SHORTCUTS } from '@/constants/posShortcuts';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { fetchWalkInCustomer, selectCustomer } from '@/store/posCustomerSlice';
import type { PosCartState } from '@/types/billing';
import type { PaymentMethod } from '@/types/enums';
import type { ProductResponse } from '@/types/product';
import { cartTotals } from '@/utils/posCart';
import { showApiError } from '@/utils/apiError';
import { useConfirm } from '@/components/ui/ConfirmDialog';
import { usePrompt } from '@/components/ui/PromptDialog';
import { usePosSession } from '@/hooks/usePosSession';
import { usePosHardware } from '@/pages/pos/PosHardwareContext';
import { useFeatures } from '@/core/features/useFeatures';
import { FEATURE } from '@/core/features/featureCodes';
import type { PaymentRequest } from '@/types/billing';
import { emptyCart, usePosCart } from '@/pages/pos/usePosCart';
import {
  collectGatewayPayment,
  finalizeGatewayInvoice,
} from '@/utils/posGatewayCheckout';
import { isPosOfflineModeEnabled } from '@/utils/posOffline';
import {
  enqueuePendingSale,
  isLikelyNetworkError,
  listPendingSales,
  removePendingSale,
} from '@/utils/posOfflineQueue';

import { notify } from '@/core/toast/notify';
const PAYMENT_METHODS: PaymentMethod[] = ['CASH', 'UPI', 'CARD', 'WALLET', 'CREDIT', 'RAZORPAY', 'STRIPE', 'PAYTM'];

export interface PosBillingContextValue {
  currencyCode: string;
  searchRef: React.RefObject<HTMLInputElement | null>;
  search: string;
  setSearch: (v: string) => void;
  products: ProductResponse[];
  productIndex: number;
  setProductIndex: React.Dispatch<React.SetStateAction<number>>;
  addHighlightedProduct: () => void;
  addProduct: (product: ProductResponse, qty?: number) => void;
  addProductWithModifiers: (
    product: ProductResponse,
    modifiers: import('@/utils/posModifiers').PosModifierOption[],
    extraTotal: number,
    qty?: number,
  ) => void;
  storeId: number;
  cart: PosCartState;
  totals: ReturnType<typeof cartTotals>;
  selectedIndex: number;
  setSelectedIndex: React.Dispatch<React.SetStateAction<number>>;
  updateQty: (index: number, qty: number) => void;
  removeItem: (index: number) => void;
  clearCart: () => void;
  paymentOpen: boolean;
  setPaymentOpen: (v: boolean) => void;
  discountOpen: boolean;
  setDiscountOpen: (v: boolean) => void;
  helpOpen: boolean;
  setHelpOpen: (v: boolean) => void;
  qtyEdit: string;
  setQtyEdit: (v: string) => void;
  quickCustomerOpen: boolean;
  setQuickCustomerOpen: (v: boolean) => void;
  paymentMethod: PaymentMethod;
  setPaymentMethod: (m: PaymentMethod) => void;
  paidAmount: string;
  setPaidAmount: (v: string) => void;
  billDiscount: string;
  setBillDiscount: (v: string) => void;
  couponCode: string;
  setCouponCode: (v: string) => void;
  loyaltyRedeem: string;
  setLoyaltyRedeem: (v: string) => void;
  checkoutGateways: StorePaymentGateway[];
  gatewayProvider: string | null;
  setGatewayProvider: (v: string | null) => void;
  splitPayMode: boolean;
  setSplitPayMode: (v: boolean) => void;
  splitPayments: PaymentRequest[];
  setSplitPayments: React.Dispatch<React.SetStateAction<PaymentRequest[]>>;
  splitPaymentAllowed: boolean;
  processing: boolean;
  couponLoading: boolean;
  openPayment: () => void;
  openDiscount: () => void;
  holdBill: () => void;
  completeSale: () => void;
  applyBillDiscountFromField: () => void;
  applyCoupon: () => Promise<void>;
  setCustomerId: (id: number | undefined) => void;
  customerIsWalkIn: boolean;
  fmt: (amount: number | string | null | undefined) => string;
}

const PosBillingCtx = createContext<PosBillingContextValue | null>(null);

export function usePosBilling() {
  const ctx = useContext(PosBillingCtx);
  if (!ctx) throw new Error('usePosBilling requires PosBillingProvider');
  return ctx;
}

interface ProviderProps {
  children: ReactNode;
  fmt: (amount: number | string | null | undefined) => string;
}

export function PosBillingProvider({ children, fmt }: ProviderProps) {
  const { confirm, dialog: confirmDialog } = useConfirm();
  const { prompt, dialog: promptDialog } = usePrompt();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const store = useAppSelector((s) => s.app.selectedStore);
  const { counterId, activeShiftId } = usePosSession();
  const { kickCashDrawerIfEnabled } = usePosHardware();
  const posCustomer = useAppSelector((s) => s.posCustomer);
  const { setModalOpen, setFocusZone, setStatusHint } = useKeyboardContext();

  const searchRef = useRef<HTMLInputElement>(null);
  const {
    cart,
    loadCart,
    products,
    search,
    setSearch,
    searchProducts,
    addProduct,
    addProductWithModifiers,
    updateQty,
    updateLineDiscount,
    removeItem,
    clearCart,
    totals,
    setCustomerId,
    applyBillDiscount,
    applyCoupon: applyCouponApi,
    holdBill: holdBillApi,
  } = usePosCart(store?.id);

  const [productIndex, setProductIndex] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [discountOpen, setDiscountOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [qtyEdit, setQtyEdit] = useState('');
  const [quickCustomerOpen, setQuickCustomerOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('CASH');
  const [paidAmount, setPaidAmount] = useState('');
  const [billDiscount, setBillDiscount] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [processing, setProcessing] = useState(false);
  const [couponLoading, setCouponLoading] = useState(false);
  const [loyaltyRedeem, setLoyaltyRedeem] = useState('');
  const [checkoutGateways, setCheckoutGateways] = useState<StorePaymentGateway[]>([]);
  const [gatewayProvider, setGatewayProvider] = useState<string | null>(null);
  const [splitPayMode, setSplitPayMode] = useState(false);
  const [splitPayments, setSplitPayments] = useState<PaymentRequest[]>([]);
  const displayTokenRef = useRef<string | null>(null);
  const { hasFeature } = useFeatures();
  const splitPaymentAllowed = hasFeature(FEATURE.POS_SPLIT_PAYMENT);

  const currencyCode = store?.currencyCode ?? 'INR';
  const selectedCustomer = posCustomer.selected;
  const customerIsWalkIn = selectedCustomer?.walkIn ?? true;

  const modalOpen =
    paymentOpen || discountOpen || helpOpen || !!qtyEdit || quickCustomerOpen;

  useEffect(() => {
    setModalOpen(modalOpen);
  }, [modalOpen, setModalOpen]);

  useEffect(() => {
    dispatch(fetchWalkInCustomer());
  }, [dispatch]);

  useEffect(() => {
    const raw = sessionStorage.getItem('fattahone_resume_cart');
    if (raw) {
      try {
        const resumed = JSON.parse(raw) as PosCartState;
        loadCart(resumed);
        sessionStorage.removeItem('fattahone_resume_cart');
        if (resumed.customerId) {
          customersApi.get(resumed.customerId).then((c) => dispatch(selectCustomer(c))).catch(() => {});
        }
        notify.success('Held bill loaded');
      } catch {
        sessionStorage.removeItem('fattahone_resume_cart');
      }
    }
  }, [dispatch, loadCart]);

  useEffect(() => {
    const t = setTimeout(() => searchProducts(search), 280);
    return () => clearTimeout(t);
  }, [search, searchProducts]);

  useEffect(() => {
    setProductIndex(0);
  }, [products]);

  useEffect(() => {
    if (selectedIndex >= cart.items.length) {
      setSelectedIndex(Math.max(0, cart.items.length - 1));
    }
  }, [cart.items.length, selectedIndex]);

  useEffect(() => {
    if (!store?.id) return;
    posApi
      .getHardwareConfig(store.id, counterId)
      .then((hw) => {
        if (!hw.customerDisplayEnabled) return;
        return posApi.createDisplaySession(store.id, counterId).then(({ sessionToken }) => {
          displayTokenRef.current = sessionToken;
          sessionStorage.setItem('fattahone_display_token', sessionToken);
        });
      })
      .catch(() => {});
  }, [store?.id, counterId]);

  useEffect(() => {
    const token = displayTokenRef.current ?? sessionStorage.getItem('fattahone_display_token');
    if (!token) return;
    const cartJson = JSON.stringify({
      items: cart.items.map((i) => ({
        name: i.name,
        quantity: i.quantity,
        lineTotal: i.lineTotal,
      })),
    });
    posApi.updateDisplay(token, cartJson, totals.total).catch(() => {});
  }, [cart.items, totals.total]);

  const addHighlightedProduct = useCallback(() => {
    const p = products[productIndex];
    if (p) addProduct(p);
  }, [products, productIndex, addProduct]);

  const openPayment = useCallback(() => {
    if (!cart.items.length || !store) return;
    if (!counterId) {
      notify.error('Select a billing counter before payment');
      return;
    }
    if (hasFeature(FEATURE.POS_SHIFT_MANAGEMENT) && !activeShiftId) {
      notify.warning('Open a cashier shift before taking payment');
      navigate('/pos/shift');
      return;
    }
    if (paymentMethod === 'WALLET' && customerIsWalkIn) {
      notify.error('Select a registered customer for wallet payment');
      return;
    }
    setBillDiscount(String(cart.discountAmount || ''));
    setCouponCode(cart.couponCode ?? '');
    setLoyaltyRedeem('');
    setGatewayProvider(null);
    setPaidAmount(String(totals.total));
    void posApi
      .checkoutGateways(store.id)
      .then(setCheckoutGateways)
      .catch(() => setCheckoutGateways([]));
    if (hasFeature(FEATURE.POS_PROMOTIONS_ENGINE)) {
      void billingApi
        .evaluatePromotions(
          store.id,
          totals.itemsTotal,
          cart.items.map((i) => ({
            productId: i.productId,
            quantity: i.quantity,
            unitPrice: i.unitPrice,
            taxPercentage: i.taxPercentage,
            discountAmount: i.discountAmount,
          })),
        )
        .then((res) => {
          if (res.discountAmount > 0) {
            applyBillDiscount(res.discountAmount);
            setBillDiscount(String(res.discountAmount));
            notify.success(`Promotion discount ${fmt(res.discountAmount)} applied`);
          }
        })
        .catch(() => {});
    }
    setSplitPayMode(false);
    setSplitPayments([
      { paymentMethod: 'CASH', amount: totals.total },
      { paymentMethod: 'UPI', amount: 0 },
    ]);
    setPaymentOpen(true);
    setFocusZone('payment');
    setStatusHint('Payment: ↑↓ method · Enter complete · Esc close');
  }, [
    cart,
    customerIsWalkIn,
    paymentMethod,
    store,
    totals.total,
    totals.itemsTotal,
    applyBillDiscount,
    fmt,
    hasFeature,
    setFocusZone,
    setStatusHint,
    counterId,
    activeShiftId,
    navigate,
  ]);

  const openDiscount = useCallback(() => {
    setBillDiscount(String(cart.discountAmount));
    setDiscountOpen(true);
    setStatusHint('Bill discount — Enter apply · Esc close');
  }, [cart.discountAmount, setStatusHint]);

  const holdBill = useCallback(async () => {
    const ok = await holdBillApi(cart.customerId);
    if (ok) {
      setSelectedIndex(0);
      searchRef.current?.focus();
      setFocusZone('product');
    }
  }, [holdBillApi, cart.customerId, setFocusZone]);

  const applyBillDiscountFromField = useCallback(() => {
    applyBillDiscount(Number(billDiscount) || 0);
    setPaidAmount(String(cartTotals({ ...cart, discountAmount: Number(billDiscount) || 0 }).total));
  }, [applyBillDiscount, billDiscount, cart]);

  const applyCoupon = useCallback(async () => {
    const code = couponCode.trim();
    if (!code) return;
    setCouponLoading(true);
    try {
      const discount = await applyCouponApi(code);
      setBillDiscount(String(discount));
      setPaidAmount(
        String(cartTotals({ ...cart, couponCode: code, discountAmount: discount }).total),
      );
    } catch (err) {
      showApiError(err, 'Invalid or expired coupon');
    } finally {
      setCouponLoading(false);
    }
  }, [applyCouponApi, cart, couponCode]);

  const completeSale = useCallback(async () => {
    if (!store || cart.items.length === 0) return;
    if (paymentMethod === 'WALLET') {
      if (customerIsWalkIn || !cart.customerId) {
        notify.error('Select a registered customer for wallet payment');
        return;
      }
      const balance = selectedCustomer?.walletBalance ?? 0;
      if (balance < totals.total) {
        notify.error(`Insufficient wallet balance (${fmt(balance)} available)`);
        return;
      }
    }
    if (paymentMethod === 'CREDIT') {
      if (customerIsWalkIn || !cart.customerId) {
        notify.error('Select a credit-enabled customer');
        return;
      }
      if (!selectedCustomer?.creditEnabled) {
        notify.error('Customer is not enabled for credit sales');
        return;
      }
      const available = selectedCustomer?.creditAvailable ?? 0;
      if (available < totals.total) {
        notify.error(`Insufficient credit (${fmt(available)} available)`);
        return;
      }
    }
    const redeemPts = loyaltyRedeem ? Number(loyaltyRedeem) : 0;
    if (redeemPts > 0) {
      const balance = selectedCustomer?.loyaltyPointsBalance ?? selectedCustomer?.loyaltyPoints ?? 0;
      if (balance < redeemPts) {
        notify.error(`Insufficient loyalty points (${balance} available)`);
        return;
      }
    }
    const paid =
      paymentMethod === 'WALLET' ||
      paymentMethod === 'CREDIT' ||
      gatewayProvider != null
        ? totals.total
        : Number(paidAmount) || totals.total;
    if (paid < totals.total && paymentMethod === 'CASH') {
      notify.error('Insufficient payment');
      return;
    }
    setProcessing(true);
    const clientRequestId = crypto.randomUUID();
    let invoiceBody: Parameters<typeof billingApi.createInvoice>[0] | undefined;
    let payments: PaymentRequest[] = [];
    try {
      const heldBillIdRaw = sessionStorage.getItem('fattahone_resume_held_bill_id');
      const heldBillId = heldBillIdRaw ? Number(heldBillIdRaw) : undefined;
      const shiftId = activeShiftId;
      invoiceBody = {
        storeId: store.id,
        counterId,
        customerId: cart.customerId,
        discountAmount: cart.discountAmount,
        couponCode: cart.couponCode,
        loyaltyPointsToRedeem: redeemPts > 0 ? redeemPts : undefined,
        gstInclusive: cart.gstInclusive,
        notes: cart.notes,
        heldBillId: heldBillId && !Number.isNaN(heldBillId) ? heldBillId : undefined,
        shiftId,
        clientRequestId,
        items: cart.items.map((i) => ({
          productId: i.productId,
          quantity: i.quantity,
          unitPrice: i.unitPrice,
          taxPercentage: i.taxPercentage,
          discountAmount: i.discountAmount,
        })),
      };

      const finishSale = async (invoiceId: number, invoiceNumber: string, paidWith: PaymentRequest[]) => {
        notify.success(`Invoice ${invoiceNumber} created`);
        await kickCashDrawerIfEnabled(paidWith.map((p) => p.paymentMethod));
        try {
          await billingApi.openPrintWindow(invoiceId);
        } catch {
          /* optional */
        }
        loadCart(emptyCart());
        sessionStorage.removeItem('fattahone_resume_held_bill_id');
        setPaymentOpen(false);
        setPaidAmount('');
        setCouponCode('');
        setLoyaltyRedeem('');
        setSelectedIndex(0);
        searchRef.current?.focus();
        setFocusZone('product');
        setStatusHint('Sale complete — F1 scan/search next item');
      };

      if (gatewayProvider) {
        const invoice = await billingApi.createInvoice({ ...invoiceBody, payments: [] });
        try {
          const gatewayPayment = await collectGatewayPayment({
            storeId: store.id,
            invoiceId: invoice.id,
            provider: gatewayProvider,
            amount: totals.total,
            currency: currencyCode,
            confirm: (message) => confirm({ message, title: 'Confirm payment' }),
          });
          await finalizeGatewayInvoice(invoice.id, gatewayPayment);
          await finishSale(invoice.id, invoice.invoiceNumber, [{ paymentMethod: gatewayProvider as PaymentMethod, amount: totals.total }]);
        } catch (err) {
          if (err instanceof Error && err.message === 'Payment cancelled') {
            notify.error('Payment cancelled — invoice saved as pending');
          } else {
            showApiError(err, 'Gateway payment failed');
          }
        }
        return;
      }

      const paymentsPayload: PaymentRequest[] = splitPayMode
        ? splitPayments
            .filter((p) => p.amount > 0)
            .map((p) => ({ ...p, amount: Number(p.amount) }))
        : [{ paymentMethod, amount: paid }];

      payments = paymentsPayload;

      if (splitPayMode) {
        const sum = payments.reduce((s, p) => s + p.amount, 0);
        if (Math.abs(sum - totals.total) > 0.02) {
          notify.error(`Split payments (${fmt(sum)}) must equal total ${fmt(totals.total)}`);
          return;
        }
        if (payments.length < 2) {
          notify.error('Split payment requires at least two tenders');
          return;
        }
      }

      const invoice = await billingApi.createInvoice({
        ...invoiceBody,
        payments,
      });
      await finishSale(invoice.id, invoice.invoiceNumber, payments);
    } catch (err) {
      if (isPosOfflineModeEnabled() && !gatewayProvider && invoiceBody && isLikelyNetworkError(err)) {
        try {
          await enqueuePendingSale({ ...invoiceBody, payments });
          notify.success('Sale saved offline — will sync when connection returns');
          loadCart(emptyCart());
          sessionStorage.removeItem('fattahone_resume_held_bill_id');
          setPaymentOpen(false);
          setPaidAmount('');
          setCouponCode('');
          setLoyaltyRedeem('');
          setSelectedIndex(0);
          searchRef.current?.focus();
        } catch (queueErr) {
          showApiError(queueErr, 'Could not queue offline sale');
        }
      } else {
        showApiError(err, 'Checkout failed');
      }
    } finally {
      setProcessing(false);
    }
  }, [
    store,
    counterId,
    cart,
    paymentMethod,
    customerIsWalkIn,
    selectedCustomer,
    totals.total,
    paidAmount,
    loyaltyRedeem,
    gatewayProvider,
    splitPayMode,
    splitPayments,
    currencyCode,
    fmt,
    loadCart,
    setFocusZone,
    setStatusHint,
    kickCashDrawerIfEnabled,
  ]);

  const flushOfflineQueue = useCallback(async () => {
    if (!isPosOfflineModeEnabled() || !navigator.onLine) return;
    const pending = await listPendingSales();
    for (const sale of pending) {
      try {
        const invoice = await billingApi.createInvoice(sale.body);
        await removePendingSale(sale.clientRequestId);
        notify.success(`Synced offline sale ${invoice.invoiceNumber}`);
      } catch (err) {
        if (!isLikelyNetworkError(err)) {
          await removePendingSale(sale.clientRequestId);
          showApiError(err, 'Offline sale rejected by server');
        }
        break;
      }
    }
  }, []);

  useEffect(() => {
    void flushOfflineQueue();
    const onOnline = () => void flushOfflineQueue();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [flushOfflineQueue]);

  const closeModals = useCallback(() => {
    setPaymentOpen(false);
    setDiscountOpen(false);
    setHelpOpen(false);
    setQtyEdit('');
    setQuickCustomerOpen(false);
    setFocusZone('product');
    searchRef.current?.focus();
    setStatusHint('Search · ↑↓ cart · Payment');
  }, [setFocusZone, setStatusHint]);

  useKeyboardShortcuts(
    [
      {
        id: 'pos-f1-search',
        keys: ['f1'],
        label: POS_SHORTCUTS.PRODUCT_SEARCH,
        description: 'Focus product search',
        category: 'Billing',
        priority: 1,
        handler: () => {
          searchRef.current?.focus();
          setFocusZone('product');
          setStatusHint('Product search — ↑↓ select · Enter add');
        },
      },
      {
        id: 'pos-f2-customer',
        keys: ['f2'],
        label: POS_SHORTCUTS.CUSTOMER_SEARCH,
        description: 'Focus customer search',
        category: 'Customer',
        handler: () => {
          focusElement('pos-customer-search');
          setFocusZone('customer');
          setStatusHint('Customer search — ↑↓ results · Enter select');
        },
      },
      {
        id: 'pos-f3-create-customer',
        keys: ['f3'],
        label: POS_SHORTCUTS.CUSTOMER_CREATE,
        description: 'Create customer',
        category: 'Customer',
        allowInModal: true,
        handler: () => setQuickCustomerOpen(true),
      },
      {
        id: 'pos-f4-hold',
        keys: ['f4', 'ctrl+h'],
        label: POS_SHORTCUTS.HOLD_BILL,
        description: 'Hold bill',
        category: 'Billing',
        enabled: () => cart.items.length > 0,
        handler: () => void holdBill(),
      },
      {
        id: 'pos-f5-held-page',
        keys: ['f5'],
        label: POS_SHORTCUTS.HELD_BILLS,
        description: 'Held bills',
        category: 'Hold Bills',
        handler: () => navigate('/pos/held'),
      },
      {
        id: 'pos-f6-payment',
        keys: ['f6', 'ctrl+p'],
        label: POS_SHORTCUTS.PAYMENT,
        description: 'Payment',
        category: 'Payment',
        enabled: () => cart.items.length > 0,
        handler: () => openPayment(),
      },
      {
        id: 'pos-f7-complete',
        keys: ['f7', 'ctrl+enter'],
        label: POS_SHORTCUTS.COMPLETE_SALE,
        description: 'Complete sale',
        category: 'Billing',
        enabled: () => cart.items.length > 0,
        handler: () => {
          if (paymentOpen) void completeSale();
          else openPayment();
        },
      },
      {
        id: 'pos-f8-return',
        keys: ['f8'],
        label: POS_SHORTCUTS.RETURN,
        description: 'Returns',
        category: 'Billing',
        handler: () => navigate('/pos/returns'),
      },
      {
        id: 'pos-f9-discount',
        keys: ['f9'],
        label: POS_SHORTCUTS.DISCOUNT,
        description: 'Discount',
        category: 'Billing',
        handler: () => openDiscount(),
      },
      {
        id: 'pos-clear',
        keys: ['ctrl+delete'],
        label: POS_SHORTCUTS.CLEAR_CART,
        description: 'Clear cart',
        category: 'Billing',
        handler: () => {
          clearCart();
          setSelectedIndex(0);
        },
      },
      {
        id: 'pos-help',
        keys: ['ctrl+/'],
        label: POS_SHORTCUTS.HELP,
        description: 'Shortcuts help',
        category: 'Navigation',
        allowInModal: true,
        handler: () => setHelpOpen(true),
      },
      {
        id: 'pos-esc',
        keys: ['escape'],
        label: 'Esc',
        description: 'Close',
        category: 'Navigation',
        allowInModal: true,
        handler: () => closeModals(),
      },
      {
        id: 'pos-delete-line',
        keys: ['delete'],
        label: POS_SHORTCUTS.REMOVE_LINE,
        description: 'Remove line',
        category: 'Cart',
        enabled: () => cart.items.length > 0 && !modalOpen,
        handler: () => {
          removeItem(selectedIndex);
          setSelectedIndex((i) => Math.max(0, i - 1));
        },
      },
      {
        id: 'pos-ctrl-q',
        keys: ['ctrl+q'],
        label: POS_SHORTCUTS.QUICK_QTY,
        description: 'Edit qty',
        category: 'Cart',
        enabled: () => cart.items.length > 0,
        handler: () => {
          const item = cart.items[selectedIndex];
          if (item) setQtyEdit(String(item.quantity));
        },
      },
      {
        id: 'pos-ctrl-d',
        keys: ['ctrl+d'],
        label: POS_SHORTCUTS.LINE_DISCOUNT,
        description: 'Line discount',
        category: 'Cart',
        enabled: () => cart.items.length > 0 && !modalOpen,
        handler: () => {
          const item = cart.items[selectedIndex];
          if (!item) return;
          void prompt({
            title: 'Line discount',
            label: 'Discount amount',
            defaultValue: String(item.discountAmount ?? 0),
          }).then((raw) => {
            if (raw == null) return;
            updateLineDiscount(selectedIndex, Number(raw) || 0);
          });
        },
      },
    ],
    [
      cart.items.length,
      selectedIndex,
      modalOpen,
      paymentOpen,
      holdBill,
      openPayment,
      openDiscount,
      completeSale,
      clearCart,
      removeItem,
      updateLineDiscount,
      prompt,
      addHighlightedProduct,
      closeModals,
      navigate,
      setFocusZone,
      setStatusHint,
    ],
  );

  const value = useMemo<PosBillingContextValue>(
    () => ({
      currencyCode,
      searchRef,
      search,
      setSearch,
      products,
      productIndex,
      setProductIndex,
      addHighlightedProduct,
      addProduct,
      addProductWithModifiers,
      storeId: store!.id,
      cart,
      totals,
      selectedIndex,
      setSelectedIndex,
      updateQty,
      removeItem,
      clearCart,
      paymentOpen,
      setPaymentOpen,
      discountOpen,
      setDiscountOpen,
      helpOpen,
      setHelpOpen,
      qtyEdit,
      setQtyEdit,
      quickCustomerOpen,
      setQuickCustomerOpen,
      paymentMethod,
      setPaymentMethod,
      paidAmount,
      setPaidAmount,
      billDiscount,
      setBillDiscount,
      couponCode,
      setCouponCode,
      loyaltyRedeem,
      setLoyaltyRedeem,
      checkoutGateways,
      gatewayProvider,
      setGatewayProvider,
      splitPayMode,
      setSplitPayMode,
      splitPayments,
      setSplitPayments,
      splitPaymentAllowed,
      processing,
      couponLoading,
      openPayment,
      openDiscount,
      holdBill,
      completeSale,
      applyBillDiscountFromField,
      applyCoupon,
      setCustomerId,
      customerIsWalkIn,
      fmt,
    }),
    [
      currencyCode,
      search,
      products,
      productIndex,
      cart,
      totals,
      selectedIndex,
      paymentOpen,
      discountOpen,
      helpOpen,
      qtyEdit,
      quickCustomerOpen,
      paymentMethod,
      paidAmount,
      billDiscount,
      couponCode,
      loyaltyRedeem,
      checkoutGateways,
      gatewayProvider,
      splitPayMode,
      splitPayments,
      splitPaymentAllowed,
      processing,
      couponLoading,
      customerIsWalkIn,
      fmt,
      addHighlightedProduct,
      addProduct,
      addProductWithModifiers,
      store,
      updateQty,
      removeItem,
      clearCart,
      openPayment,
      openDiscount,
      holdBill,
      completeSale,
      applyBillDiscountFromField,
      applyCoupon,
      setCustomerId,
    ],
  );

  return (
    <PosBillingCtx.Provider value={value}>
      {children}
      {confirmDialog}
      {promptDialog}
    </PosBillingCtx.Provider>
  );
}

export { PAYMENT_METHODS };
